import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, ArrowRight, Gavel, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";

const EFFECT = {
  clears: { label: "clears this check", cls: "text-[var(--c-green)] border-[var(--c-green)]/50 bg-[var(--c-green)]/10" },
  "clears-but-breaks-others": { label: "clears it, opens others", cls: "text-[var(--c-amber)] border-[var(--c-amber)]/50 bg-[var(--c-amber)]/10" },
  "no-effect": { label: "does not clear it", cls: "text-[var(--c-muted)] border-[var(--c-border)]" },
};

/** The answer to "it says it cannot be cleared — so what do I actually change?". Each row is a
 *  real design input with its allowed band, the value that moves this check, and the field to
 *  go and edit. Applying a row writes that exact value and re-validates. */
export default function FixGuideDialog({ projectId, issue, guide, open, onOpenChange, onApplied }) {
  const [data, setData] = useState(guide || null);
  const [busy, setBusy] = useState(null);
  const [waiving, setWaiving] = useState("");
  const [auto, setAuto] = useState(null);      // the proposal awaiting confirmation

  useEffect(() => { setData(guide || null); }, [guide, issue?.id]);

  useEffect(() => {
    if (!open || guide || !projectId || !issue) return;
    api.get(`/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/fix-guide`)
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(fmtErr(e)));
  }, [open, guide, projectId, issue]);

  // Automatic fix — two steps on purpose: propose, then commit. The fix is automatic, the
  // decision is not, so nothing is written to the design until the engineer confirms.
  const proposeAuto = async () => {
    setBusy("auto");
    try {
      const { data: res } = await api.post(
        `/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/auto-fix`, { confirm: false });
      setAuto(res);
      if (!res.ok) toast.warning(res.message || res.reason, { duration: 9000 });
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); }
    finally { setBusy(null); }
  };

  const confirmAuto = async () => {
    setBusy("auto-commit");
    try {
      const { data: res } = await api.post(
        `/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/auto-fix`, { confirm: true });
      onApplied?.(res);
      toast.success(res.message || "Fix applied");
      setAuto(null);
      onOpenChange(false);
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); }
    finally { setBusy(null); }
  };

  const applyLever = async (lv) => {
    setBusy(lv.field);
    try {
      const { data: res } = await api.post(
        `/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/apply-fix`,
        { inputs_set: { [lv.field]: lv.suggested } });
      onApplied?.(res);
      const fr = res.fix_result || {};
      if (fr.resolved) {
        toast.success(`${fr.check_id} cleared — ${lv.label} set to ${lv.suggested}`);
        onOpenChange(false);
      } else {
        toast.warning(`${lv.label} set to ${lv.suggested}, but ${data?.check_id} is still open — try another lever or waive it`, { duration: 8000 });
        api.get(`/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/fix-guide`)
          .then(({ data: d }) => setData(d)).catch(() => {});
      }
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); }
    finally { setBusy(null); }
  };

  const applySpec = async (sl) => {
    setBusy(sl.label);
    try {
      const { data: res } = await api.post(
        `/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/apply-fix`,
        { specs_patch: sl.specs_patch });
      onApplied?.(res);
      toast.success(`Specification revision applied — ${(sl.fields || []).map((f) => `${f.label || f.field} → ${f.suggested}`).join(", ") || Object.entries(sl.specs_patch).map(([k, v]) => `${k.replace(/_/g, " ")} → ${v}`).join(", ")} · team notified`);
      onOpenChange(false);
    } catch (e) { toast.error(fmtErr(e), { duration: 9000 }); }
    finally { setBusy(null); }
  };

  const waive = async () => {
    setBusy("waive");
    try {
      await api.post(`/validation/${projectId}/issues/${encodeURIComponent(issue.id)}/waive`,
        { justification: waiving.trim() });
      toast.success("Issue waived — the justification is on the project record");
      onOpenChange(false);
      onApplied?.(null);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(null); }
  };

  const levers = data?.levers || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fix-guide-dialog"
        className="max-h-[88vh] max-w-3xl overflow-y-auto border-[var(--c-border)] bg-[var(--c-surface)]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-[var(--c-text)]">
            <Compass size={18} className="text-[var(--c-accent)]" />
            How to clear <span className="font-mono text-[var(--c-accent)]">{data?.check_id || issue?.check_id}</span>
          </DialogTitle>
        </DialogHeader>

        <p className="m-0 text-[12.5px] leading-relaxed text-[var(--c-text)]" data-testid="fix-guide-verdict">
          {data?.verdict || "Sweeping each design input across its allowed band…"}
        </p>
        {(data?.calculated || data?.required) && (
          <div className="font-mono text-[11px] text-[var(--c-muted)]">
            calculated <span className="text-[var(--c-amber)]">{data.calculated}</span> · required{" "}
            <span className="text-[var(--c-text)]">{data.required}</span>
          </div>
        )}

        {/* one-click automatic fix, gated by an explicit confirmation */}
        <div className="rounded-lg border border-[var(--c-accent)]/40 bg-[var(--c-accent)]/5 p-3"
          data-testid="auto-fix-panel">
          {!auto && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12px] text-[var(--c-text)]">
                Let Formigen work out the smallest change that clears this, then confirm it.
              </span>
              <button data-testid="auto-fix-propose" disabled={busy === "auto"} onClick={proposeAuto}
                className="rounded-sm bg-[var(--c-accent)] px-3 py-1.5 text-[11px] font-medium text-[var(--c-bg)] disabled:opacity-50">
                {busy === "auto" ? "Working it out…" : "Propose automatic fix"}
              </button>
            </div>
          )}
          {auto && auto.ok && (
            <div data-testid="auto-fix-proposal">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
                {auto.how === "intake" ? "Proposed Intake update — confirm to apply"
                  : `Proposed ${auto.how === "combination" ? "combination" : "change"} — confirm to apply`}
              </div>
              {auto.how === "intake" && (
                <div className="mt-1 text-[11.5px] text-[var(--c-text)]" data-testid="auto-fix-intake-note">
                  {auto.proposal_label}
                  {auto.requires_approval && (
                    <span className="ml-1.5 rounded-sm border border-[var(--c-amber)]/50 px-1.5 py-0.5 font-mono text-[9px] uppercase text-[var(--c-amber)]">
                      needs client approval
                    </span>
                  )}
                </div>
              )}
              {(auto.spec_changes || []).map((c) => (
                <div key={c.field} className="mt-1 font-mono text-[12px] text-[var(--c-text)]"
                  data-testid={`auto-fix-spec-change-${c.field}`}>
                  Intake · {c.label}: <span className="text-[var(--c-muted)]">{String(c.current)}</span>
                  {" → "}<span className="text-[var(--c-green)]">{String(c.suggested)}{c.unit ? ` ${c.unit}` : ""}</span>
                </div>
              ))}
              {(auto.changes || []).map((c) => (
                <div key={c.field} className="mt-1 font-mono text-[12px] text-[var(--c-text)]"
                  data-testid={`auto-fix-change-${c.field}`}>
                  {c.label}: <span className="text-[var(--c-muted)]">{String(c.from)}</span>
                  {" → "}<span className="text-[var(--c-green)]">{String(c.to)}</span>
                </div>
              ))}
              <div className="mt-1.5 text-[11px] text-[var(--c-muted)]">
                {auto.clears ? "Clears this check" : "Does not fully clear this check"} · blockers{" "}
                {auto.blockers_now} → {auto.blockers_after}
                {auto.health_after != null && <> · health after {auto.health_after}</>}
                {auto.blockers_after > auto.blockers_now &&
                  <b className="text-[var(--c-amber)]"> · opens other issues</b>}
                {auto.notifies && <> · the project team is notified automatically</>}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button data-testid="auto-fix-confirm" disabled={busy === "auto-commit"} onClick={confirmAuto}
                  className="rounded-sm bg-[var(--c-green)] px-3 py-1.5 text-[11px] font-medium text-white disabled:opacity-50">
                  {busy === "auto-commit" ? "Applying…" : "Confirm and apply"}
                </button>
                <button data-testid="auto-fix-cancel" onClick={() => setAuto(null)}
                  className="rounded-sm border border-[var(--c-border)] px-3 py-1.5 text-[11px] text-[var(--c-text)]">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {auto && !auto.ok && (
            <div className="text-[12px] text-[var(--c-muted)]" data-testid="auto-fix-impossible">
              {auto.message || auto.reason}
            </div>
          )}
        </div>

        {(data?.combo && (data.combo.fields || []).length > 0) && (
          <div className="rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)]/50 p-3"
            data-testid="fix-combo">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
              Combination that clears it — no single input does
            </div>
            {data.combo.fields.map((f) => (
              <div key={f.field} className="mt-1 font-mono text-[12px] text-[var(--c-text)]">
                {f.label}: <span className="text-[var(--c-muted)]">{String(f.current)}</span>
                {" → "}<span className="text-[var(--c-green)]">{String(f.suggested)}</span>
                {f.unit ? ` ${f.unit}` : ""}
              </div>
            ))}
          </div>
        )}

        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
          Design inputs that clear it — swept one at a time
        </div>
        {!levers.length && (
          <div className="rounded-sm border border-dashed border-[var(--c-border)] p-3 text-[12px] text-[var(--c-muted)]"
            data-testid="fix-guide-no-levers">
            No single design input clears this check inside its allowed band — use the automatic
            fix above, revise the guarantee in Intake, or waive it with a justification.
          </div>
        )}
        {levers.map((lv) => (
          <div key={lv.field} data-testid={`fix-lever-${lv.field}`}
            className="rounded-lg border border-[var(--c-border)] bg-[var(--c-bg)]/50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-medium text-[var(--c-text)]">{lv.label}</span>
              <span className={`rounded-sm border px-1.5 py-0.5 font-mono text-[9px] uppercase ${EFFECT[lv.effect]?.cls}`}>
                {EFFECT[lv.effect]?.label}
              </span>
              <span className="ml-auto font-mono text-[11px] text-[var(--c-muted)]">
                {lv.current}{lv.unit && ` ${lv.unit}`} <ArrowRight size={10} className="inline" />{" "}
                <span className="text-[var(--c-accent)]">{lv.suggested}{lv.unit && ` ${lv.unit}`}</span>
              </span>
            </div>
            <div className="mt-1 font-mono text-[10.5px] text-[var(--c-muted)]">
              allowed {lv.min}–{lv.max}{lv.unit && ` ${lv.unit}`} · {lv.direction} ·{" "}
              blockers {lv.blockers_now} → {lv.blockers_after}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Button size="sm" data-testid={`fix-lever-apply-${lv.field}`} disabled={!!busy || lv.effect === "no-effect"}
                onClick={() => applyLever(lv)}
                className="h-7 rounded-sm bg-[var(--c-green)] text-[10px] text-white hover:bg-[var(--c-green)]/85 disabled:opacity-40">
                {busy === lv.field ? "Applying…" : `Set to ${lv.suggested}`}
              </Button>
              <Link data-testid={`fix-lever-open-${lv.field}`} to={`/projects/${projectId}?tab=design&focus=${lv.field}`}
                onClick={() => onOpenChange(false)}
                className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[10px] text-[var(--c-text)] hover:border-[var(--c-accent)]">
                Open in Design <ArrowRight size={10} />
              </Link>
            </div>
          </div>
        ))}

        {(data?.spec_levers || []).length > 0 && (
          <>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
              Specification side — moves a guarantee
            </div>
            {data.spec_levers.map((sl, j) => (
              <div key={j} data-testid={`fix-spec-lever-${j}`}
                className="rounded-lg border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/5 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Sparkle size={12} className="text-[var(--c-amber)]" />
                  <span className="text-[12.5px] text-[var(--c-text)]">{sl.label}</span>
                  {sl.requires_approval && (
                    <span className="rounded-sm border border-[var(--c-amber)]/50 px-1.5 py-0.5 font-mono text-[9px] uppercase text-[var(--c-amber)]">
                      needs client approval
                    </span>
                  )}
                </div>
                <div className="mt-1 font-mono text-[10.5px] text-[var(--c-muted)]">
                  {(sl.fields || []).map((f) => `${f.label || f.field}: ${f.current} → ${f.suggested}${f.unit ? ` ${f.unit}` : ""}`).join(" · ")
                    || Object.entries(sl.specs_patch).map(([k, v]) => `${k} → ${v}`).join(" · ")}
                  {sl.clears === false && " · does not clear this check"}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Button size="sm" data-testid={`fix-spec-apply-${j}`} disabled={!!busy}
                    onClick={() => applySpec(sl)}
                    className="h-7 rounded-sm bg-[var(--c-amber)] text-[10px] text-[var(--c-text)] hover:bg-[var(--c-amber)]/85">
                    Apply revision
                  </Button>
                  <Link data-testid={`fix-spec-open-${j}`}
                    to={`/projects/${projectId}?tab=intake&focus=${sl.fields?.[0]?.field || sl.fields?.[0] || ""}`
                      + (sl.fields?.[0]?.suggested != null ? `&suggest=${encodeURIComponent(sl.fields[0].suggested)}` : "")
                      + `&why=${encodeURIComponent(`${data?.check_id || ""} — ${sl.label}`.slice(0, 90))}`}
                    onClick={() => onOpenChange(false)}
                    className="flex h-7 items-center gap-1.5 rounded-sm border border-[var(--c-border)] px-2.5 text-[10px] text-[var(--c-text)] hover:border-[var(--c-accent)]">
                    Open the field in Intake <ArrowRight size={10} />
                  </Link>
                </div>
              </div>
            ))}
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--c-border)] pt-3">
          <input data-testid="fix-guide-waive-note" value={waiving}
            onChange={(e) => setWaiving(e.target.value)}
            placeholder="Or waive it — justification (min 5 characters)…"
            className="h-7 min-w-0 flex-1 rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-2 text-[11.5px] text-[var(--c-text)]" />
          <Button size="sm" data-testid="fix-guide-waive" disabled={waiving.trim().length < 5 || !!busy}
            onClick={waive}
            className="h-7 rounded-sm bg-[var(--c-amber)] text-[10px] text-[var(--c-text)] hover:bg-[var(--c-amber)]/85 disabled:opacity-40">
            <Gavel size={11} className="mr-1" /> Waive with justification
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
